import { computed, makeObservable, observable } from 'mobx';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { TaskState } from './TaskState';
import { WidgetState, WidgetsFormState } from './WidgetsFormState';

export interface FormPageProps<V extends FieldValues = FieldValues> {
  /**
   * An object representing the form state and methods, typically returned by a useForm hook.
   */
  form: UseFormReturn<V>;

  widgets?: WidgetsFormState;
}

export class FormPageState<V extends FieldValues = FieldValues> {
  readonly _widgets: WidgetsFormState;
  readonly _form: UseFormReturn<V>;
  readonly initTask = new TaskState();
  readonly submitTask = new TaskState();

  _isSubmitSuccessful?: boolean = undefined;
  _isFormDirty = false;

  constructor(props: FormPageProps<V>) {
    this._form = props.form;
    this._widgets = props.widgets ?? new WidgetsFormState();

    makeObservable(this, {
      isFetching: computed,
      _isFormDirty: observable.ref,
      isDirty: computed,
    });
  }

  set isFormDirty(flag: boolean) {
    this._isFormDirty = flag;
  }

  get isDirty() {
    return this._isFormDirty || this._widgets.isDirty;
  }

  get form() {
    return this._form;
  }

  get isFetching() {
    return this.initTask.status.isLoading || this.initTask.status.isIdle;
  }

  set isSubmitSuccessful(flag: boolean) {
    this._isSubmitSuccessful = flag;
  }

  retryFetch() {
    this.initTask.runOnce();
  }

  /**
   * For backward compatibility, keep this as validate, but it only validates widgets.
   */
  validate() {
    return this._widgets.validate();
  }

  registerWidget(key: string, state: WidgetState) {
    this._widgets.registerWidget(key, state);
  }

  getWidgetState(key: string) {
    return this._widgets.get(key);
  }
}
